// Talmia 2.0 · Data Lab v2 shared primitives.
// Small, opinionated building blocks used across the enhanced Lab screens.
// Kept isolated in /v2/_shared.jsx so the v1 files stay untouched during rollout.
import { useCallback, useEffect, useState } from "react";
import api from "../../../lib/api";

/** KPI tile in the top strip. */
export function KpiTile({ label, value, sub, tone = "", icon: Icon, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: "var(--t2-surface)",
        border: "1px solid var(--t2-border)",
        borderRadius: 10,
        padding: "12px 14px",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        boxShadow: "0 6px 18px rgba(0,0,0,.22), inset 0 1px 0 rgba(255,255,255,.05)",
        minWidth: 120,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div className="t2-mono" style={{
          fontSize: 9.5, letterSpacing: ".12em", textTransform: "uppercase",
          color: "var(--t2-brass)", fontWeight: 700,
        }}>{label}</div>
        <div className="t2-mono" style={{
          fontSize: 22, fontWeight: 800,
          color: tone ? `var(--t2-${tone})` : "var(--t2-white)",
          fontVariantNumeric: "tabular-nums", marginTop: 2,
        }}>{value}</div>
        {sub && (
          <div className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey3)", marginTop: 2 }}>
            {sub}
          </div>
        )}
      </div>
      {Icon && (
        <div style={{
          width: 30, height: 30, borderRadius: 8,
          background: "var(--t2-surface3)",
          display: "grid", placeItems: "center",
          color: tone ? `var(--t2-${tone})` : "var(--t2-orange)",
          border: "1px solid var(--t2-border2)",
          flexShrink: 0,
        }}><Icon className="w-3.5 h-3.5" /></div>
      )}
    </div>
  );
}

/** Row of KPI tiles — auto-fits. */
export function KpiBand({ children, testid }) {
  return (
    <div data-testid={testid} style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
      gap: 10,
      marginBottom: 14,
    }}>
      {children}
    </div>
  );
}

/**
 * Horizontal histogram — bars grow left-to-right, taller = more count.
 * Takes `bins = [{label, n}]`. Highlights the tallest bar.
 */
export function Histogram({ bins = [], height = 120, tone = "orange", testid }) {
  const max = Math.max(1, ...bins.map((b) => b.n || 0));
  return (
    <div data-testid={testid}
      style={{ display: "grid", gridTemplateColumns: `repeat(${bins.length || 1}, 1fr)`, gap: 6, height }}>
      {bins.map((b, i) => {
        const pct = (b.n / max) * 100;
        const isTop = b.n === max && b.n > 0;
        return (
          <div key={b.label + i}
            title={`${b.label}: ${b.n}`}
            style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", minWidth: 0 }}>
            <div className="t2-mono" style={{
              fontSize: 9.5, color: isTop ? `var(--t2-${tone})` : "var(--t2-grey3)",
              fontWeight: 700, marginBottom: 4,
              fontVariantNumeric: "tabular-nums",
            }}>{b.n || 0}</div>
            <div style={{
              width: "70%",
              height: `${pct}%`,
              minHeight: b.n > 0 ? 4 : 1,
              background: isTop ? `var(--t2-${tone})` : "var(--t2-surface3)",
              border: `1px solid ${isTop ? `var(--t2-${tone})` : "var(--t2-border2)"}`,
              borderRadius: "4px 4px 0 0",
              transition: "height .4s ease",
            }} />
            <div className="t2-mono" style={{
              marginTop: 4, fontSize: 8.5, color: "var(--t2-grey3)",
              textAlign: "center", lineHeight: 1.15, whiteSpace: "nowrap",
              overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%",
            }}>{b.label}</div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Facet chip row — toggleable filters with count badge.
 * `options: [{value, label?, count}]`, controlled by `selected` (array) + `onChange`.
 */
export function FacetChips({ options = [], selected = [], onChange, testid }) {
  const isOn = (v) => selected.includes(v);
  const toggle = (v) => {
    if (isOn(v)) onChange(selected.filter((s) => s !== v));
    else onChange([...selected, v]);
  };
  if (!options.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }} data-testid={testid}>
      {options.map((o) => {
        const on = isOn(o.value);
        return (
          <button
            key={o.value}
            onClick={() => toggle(o.value)}
            className="t2-mono"
            data-testid={`${testid}-${String(o.value).toLowerCase().replace(/\s+/g, "-")}`}
            style={{
              padding: "5px 10px",
              borderRadius: 999,
              fontSize: 10.5, fontWeight: 700,
              border: `1px solid ${on ? "var(--t2-orange)" : "var(--t2-border)"}`,
              background: on ? "var(--t2-orange-dim)" : "var(--t2-surface2)",
              color: on ? "var(--t2-orange)" : "var(--t2-grey1)",
              cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 5,
              transition: "all .12s ease",
            }}
          >
            {o.label || o.value}
            <span style={{
              fontSize: 9,
              color: on ? "var(--t2-orange)" : "var(--t2-grey4)",
              fontWeight: 600,
            }}>{o.count}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Version banner — displays at the very top of every v2 page so users know
 * they're on the enhanced experience and can flip back easily.
 */
export function V2Banner() {
  // v1 has been retired — the Data Lab is now single-track, so this banner is a no-op.
  return null;
}

export function disableV2() {
  try {
    // Explicit opt-out — v2 is now the default, so we set lab.v2=0 to route back to v1.
    localStorage.setItem("lab.v2", "0");
    const url = new URL(window.location.href);
    url.searchParams.delete("adv");
    window.location.href = url.toString();
  } catch { /* silent */ }
}

/**
 * RecordHistory — chronological webhook_log events for one Data Lab record.
 * Consumes GET /api/lab/{collection}/{doc_id}/history. Renders "Initial ingest"
 * when there's no wire history yet so the panel is never empty.
 */
export function RecordHistory({ collection, docId, testid = "record-history" }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get(`/lab/${collection}/${docId}/history?limit=100`);
      setEvents(data.events || []);
    } catch (e) { setErr(e?.response?.data?.detail || e?.message || "failed"); }
    finally { setLoading(false); }
  }, [collection, docId]);
  useEffect(() => { load(); }, [load]);

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-GB"); } catch { return String(iso); }
  };

  return (
    <div data-testid={testid} style={{
      background: "var(--t2-surface)", border: "1px solid var(--t2-border)",
      borderRadius: 10, padding: "12px 14px", marginTop: 12,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span className="t2-mono" style={{
          fontSize: 10.5, letterSpacing: ".12em", textTransform: "uppercase",
          color: "var(--t2-brass)", fontWeight: 800,
        }}>Change history</span>
        <button onClick={load} className="t2-mono" style={{
          background: "none", border: "1px solid var(--t2-border2)", borderRadius: 6,
          padding: "3px 8px", color: "var(--t2-grey1)", cursor: "pointer",
          fontSize: 10, letterSpacing: ".06em",
        }} data-testid={`${testid}-refresh`}>Refresh</button>
      </div>
      {loading && !events && <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey4)" }}>Loading history…</div>}
      {err && <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-red)" }}>Couldn&rsquo;t load history: {err}</div>}
      {events && events.length === 0 && (
        <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey4)" }}>No history yet — new events will appear here as data flows.</div>
      )}
      {events && events.length > 0 && (
        <ol style={{ listStyle: "none", padding: 0, margin: 0, borderLeft: "1px dashed var(--t2-border2)" }}>
          {events.map((ev) => (
            <li key={ev.id} style={{ padding: "6px 0 6px 14px", position: "relative" }} data-testid={`${testid}-event`}>
              <span style={{
                position: "absolute", left: -5, top: 10, width: 8, height: 8, borderRadius: 8,
                background: ev.status === "ok" ? "var(--t2-teal)" : ev.action === "seed" ? "var(--t2-brass)" : "var(--t2-red)",
              }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                <span className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-orange)", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>
                  {ev.action || "event"}
                </span>
                <span className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey3)" }}>{fmt(ev.ts)}</span>
              </div>
              {Object.keys(ev.diff || {}).length > 0 && (
                <pre style={{
                  fontFamily: "var(--t2-mono)", fontSize: 10.5, color: "var(--t2-grey1)",
                  margin: "4px 0 0", whiteSpace: "pre-wrap", wordBreak: "break-word",
                  background: "var(--t2-bg)", padding: "6px 8px", borderRadius: 6,
                  border: "1px solid var(--t2-border)",
                }}>{JSON.stringify(ev.diff, null, 1)}</pre>
              )}
              {ev.error && (
                <div className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-red)", marginTop: 3 }}>{ev.error}</div>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
